/**
 * The visitor's listing shuffle seed for this browser session.
 *
 * Browse lists send it as `shuffle_seed` so the backend orders listings in a
 * stable shuffle: the order holds across refreshes, back-navigation and
 * "Load more" pages within the session, and differs between visitors so no
 * listing is always at the bottom. sessionStorage scopes it to the tab
 * session; when storage is blocked the seed lives for the page instead.
 */

/** sessionStorage key holding the session's listing shuffle seed. */
export const LISTING_SHUFFLE_SEED_STORAGE_KEY = 'belhop_listing_shuffle_seed';

/** Largest seed the backend accepts (a signed 32-bit integer). */
export const MAX_LISTING_SHUFFLE_SEED = 2 ** 31 - 1;

let pageSeed: number | null = null;

const parseSeed = (storedValue: string | null): number | null => {
  if (storedValue === null || !/^\d+$/.test(storedValue)) return null;
  const seed = Number(storedValue);
  return seed <= MAX_LISTING_SHUFFLE_SEED ? seed : null;
};

const drawSeed = (): number => Math.floor(Math.random() * (MAX_LISTING_SHUFFLE_SEED + 1));

export const getListingShuffleSeed = (): number => {
  try {
    const storedSeed = parseSeed(window.sessionStorage.getItem(LISTING_SHUFFLE_SEED_STORAGE_KEY));
    if (storedSeed !== null) return storedSeed;
    const seed = drawSeed();
    window.sessionStorage.setItem(LISTING_SHUFFLE_SEED_STORAGE_KEY, String(seed));
    return seed;
  } catch (error) {
    // Blocked or full storage (private mode, disabled site data) throws a
    // DOMException; anything else is a real bug and must surface.
    if (!(error instanceof DOMException)) throw error;
    pageSeed ??= drawSeed();
    return pageSeed;
  }
};
